// ============================================================
// Pro-portal tenant resolution.
// ============================================================
// The pro portal is a single Cloudflare Pages build that gets served on
// many hostnames — one per tenant — using the convention
//   <tenantSubdomain>pro.<rootDomain>
// e.g. tunielecpro.gesty.tn  → tenant `tunielec`
//      acmepro.gesty.tn      → tenant `acme`
//
// We derive the tenant from `window.location.hostname` at runtime so a
// single deployment serves every tenant. Older deployments (and the
// preview URL `erp-pro-2nu.pages.dev`) used a build-time env var
// (`VITE_PRO_TENANT_SUBDOMAIN`); we keep it as a fallback for those
// hostnames and for local dev.

const FALLBACK_ROOT_DOMAIN = 'gesty.tn';
const PRO_SUFFIX = 'pro';
// The pro portal is currently a SINGLE-tenant deployment for Tunielec, so when
// the hostname can't tell us the tenant (preview URLs, a bare custom domain,
// local dev) we resolve to Tunielec rather than "unknown" — its documents must
// always carry Tunielec's company identity. `VITE_PRO_TENANT_SUBDOMAIN` still
// overrides this; make it configurable / drop the default when the portal
// serves multiple tenants.
const DEFAULT_PRO_TENANT_SUBDOMAIN = 'tunielec';

function getRootDomains(): string[] {
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  const clean = (s?: string) =>
    s?.trim().toLowerCase().replace(/^\.+/, '').replace(/\/+$/, '') || '';
  const primary = clean(env.VITE_STOREFRONT_ROOT_DOMAIN) || FALLBACK_ROOT_DOMAIN;
  // Legacy roots kept live during the gesty.tn ← lyfty.agency migration so old
  // <X>pro.<legacy> hostnames still resolve. Drop by setting
  // VITE_STOREFRONT_LEGACY_ROOT_DOMAINS="" once lyfty.agency is retired.
  const legacy = (env.VITE_STOREFRONT_LEGACY_ROOT_DOMAINS ?? 'lyfty.agency')
    .split(',')
    .map((s) => clean(s))
    .filter(Boolean);
  return [primary, ...legacy];
}

/** Strip the leftmost subdomain off a hostname, returning what was
 *  before the rootDomain. Returns null when the hostname doesn't end
 *  with the root domain (e.g. erp-pro-2nu.pages.dev, localhost). */
function leftmostLabel(hostname: string, rootDomain: string): string | null {
  const h = hostname.trim().toLowerCase();
  const root = rootDomain.toLowerCase();
  const suffix = '.' + root;
  if (!h.endsWith(suffix)) return null;
  const label = h.slice(0, -suffix.length);
  return label.length > 0 ? label : null;
}

/** Derive the tenant subdomain a pro signup should attach to.
 *  Resolution order:
 *    1. Hostname matches `<X>pro.<root>` → use `<X>`.
 *    2. Hostname matches `<X>.<root>` (no `pro` suffix) → use `<X>`
 *       (covers a hypothetical pro portal hosted directly at a tenant
 *       subdomain — never our default but reasonable to support).
 *    3. Hostname doesn't match the root domain (preview URLs, dev) →
 *       fall back to `VITE_PRO_TENANT_SUBDOMAIN`, then to
 *       `DEFAULT_PRO_TENANT_SUBDOMAIN` (the single-tenant Tunielec portal).
 *  Always returns a non-empty tenant subdomain while the portal is
 *  single-tenant. */
export function resolveProTenantSubdomain(): string {
  // SSR safety: in case this is ever evaluated outside the browser,
  // skip straight to the env-var fallback.
  if (typeof window === 'undefined') {
    const env = import.meta.env as unknown as Record<string, string | undefined>;
    return (env.VITE_PRO_TENANT_SUBDOMAIN ?? '').trim() || DEFAULT_PRO_TENANT_SUBDOMAIN;
  }
  const roots = getRootDomains();
  let label: string | null = null;
  for (const root of roots) {
    label = leftmostLabel(window.location.hostname, root);
    if (label) break;
  }
  if (label) {
    if (label.length > PRO_SUFFIX.length && label.endsWith(PRO_SUFFIX)) {
      return label.slice(0, -PRO_SUFFIX.length);
    }
    return label;
  }
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  return (env.VITE_PRO_TENANT_SUBDOMAIN ?? '').trim() || DEFAULT_PRO_TENANT_SUBDOMAIN;
}
